import { useState, useCallback, useEffect, type RefObject } from 'react'

import { useCommentData } from '../../context/CommentContext'

type UseReplyFormProps = {
  textareaRef: RefObject<HTMLTextAreaElement>
  initialExpanded?: boolean
  onManualExpansion?: (commentId: string | null) => void
}

type UseReplyFormReturn = {
  text: string
  setText: (text: string) => void
  isExpanded: boolean
  expand: () => void
  collapse: () => void
  focus: () => void
  clear: () => void
}

/**
 * Hook for managing basic reply form state and interactions.
 *
 * This hook handles the core form functionality including:
 * - Text content management
 * - Form expansion/collapse state
 * - Focus management
 * - Initial state setup
 *
 * **When to use:**
 * - When you need basic form state management for a reply textarea
 * - As a building block for more complex reply functionality
 * - When you want to handle form state independently from submission logic
 *
 * **Key Features:**
 * - Automatic focus when expanding the form
 * - Initial expansion state support
 * - Clean text clearing functionality
 * - Focus management with proper timing
 *
 * @param props - Configuration for form behavior
 * @returns Form state and interaction handlers
 *
 * @example
 * ```tsx
 * const form = useReplyForm({ textareaRef, initialExpanded: false })
 *
 * // Basic usage
 * <textarea value={form.text} onChange={(e) => form.setText(e.target.value)} />
 * <button onClick={form.expand}>Reply</button>
 * <button onClick={form.clear}>Clear</button>
 * ```
 *
 * @example
 * ```tsx
 * // Programmatic control
 * form.setText("Thank you for your feedback!")
 * form.expand()
 * form.focus()
 * ```
 */
export const useReplyForm = ({
  textareaRef,
  initialExpanded = false,
  onManualExpansion,
}: UseReplyFormProps): UseReplyFormReturn => {
  const comment = useCommentData()

  // Form state
  const [replyText, setReplyText] = useState('')
  const [isExpanded, setIsExpanded] = useState(initialExpanded)

  // Focus management
  const focus = useCallback(() => {
    if (textareaRef.current) {
      textareaRef.current.focus()
    }
  }, [textareaRef])

  const expand = useCallback(() => {
    setIsExpanded(true)
    // Coordinate with global auto-expansion state
    if (onManualExpansion) {
      onManualExpansion(comment.id)
    }
    // Focus after expansion
    requestAnimationFrame(() => focus())
  }, [focus, onManualExpansion, comment.id])

  const collapse = useCallback(() => {
    setIsExpanded(false)
  }, [])

  const clear = useCallback(() => {
    setReplyText('')
  }, [])

  // Sync local state with prop changes (for global coordination)
  useEffect(() => {
    setIsExpanded(initialExpanded)
  }, [initialExpanded, comment.id])

  // Handle initial expansion and focus
  useEffect(() => {
    if (initialExpanded) {
      textareaRef.current?.focus()
    }
  }, [textareaRef, initialExpanded])

  return {
    text: replyText,
    setText: setReplyText,
    isExpanded,
    expand,
    collapse,
    focus,
    clear,
  }
}
